import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Gavel,
  ScrollText,
  Ship,
  ShieldCheck,
  Radar,
  PackageCheck,
  ArrowRight,
} from "lucide-react";

const NODES = [
  { icon: FileText, label: "RFQ™", note: "Sourcing request", current: true },
  { icon: Gavel, label: "CommodityBid™", note: "Live auction" },
  { icon: ScrollText, label: "Purchase Order", note: "Contractual" },
  { icon: Ship, label: "FreightIQ™", note: "Freight planning" },
  { icon: ShieldCheck, label: "Inspection™", note: "Pre-shipment QC" },
  { icon: Radar, label: "Live Shipment Tracking™", note: "Real-time visibility" },
  { icon: PackageCheck, label: "Delivered", note: "Closed loop" },
];

export default function ConnectedWorkspace() {
  return (
    <section
      id="workspace"
      data-testid="workspace-section"
      className="relative py-24 sm:py-32 border-t border-black/[0.06] bg-[#0A0A0A] text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 0%, #000 35%, transparent 90%)",
        }}
      />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[480px] w-[1100px] rounded-[100%] bg-[#0044FF]/20 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="eyebrow text-white/70">Connected Workspace</span>
          <h2 className="mt-4 font-display font-black tracking-[-0.035em] text-[40px] sm:text-[56px] leading-[0.98] text-white">
            One Workspace.
            <br />
            <span className="text-white/40">The entire import operation.</span>
          </h2>
          <p className="mt-5 text-[15px] sm:text-[17px] leading-relaxed text-white/65 max-w-2xl">
            RFQ™ is the starting point. Every step of the import flow continues
            inside the same Workspace — same data, same suppliers, zero re-entry.
          </p>
        </div>

        {/* Pipeline */}
        <div className="mt-14 overflow-x-auto no-scrollbar">
          <div className="min-w-[920px] grid grid-cols-7 gap-4">
            {NODES.map((n, i) => (
              <motion.div
                key={n.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="relative"
              >
                <div
                  className={`rounded-2xl border p-4 h-full transition-colors ${
                    n.current
                      ? "bg-white text-[#0A0A0A] border-white shadow-[0_30px_70px_-30px_rgba(0,68,255,0.6)]"
                      : "bg-white/[0.04] border-white/10 hover:bg-white/[0.07]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${
                        n.current
                          ? "bg-[#0044FF]/10 text-[#0044FF]"
                          : "bg-white/10 text-white"
                      }`}
                    >
                      <n.icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <span
                      className={`font-mono text-[10px] tracking-[0.18em] ${
                        n.current ? "text-[#0044FF]" : "text-white/40"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div
                    className={`mt-3 font-display text-[14.5px] font-semibold tracking-tight ${
                      n.current ? "text-[#0A0A0A]" : "text-white"
                    }`}
                  >
                    {n.label}
                  </div>
                  <div
                    className={`mt-0.5 text-[12px] ${
                      n.current ? "text-black/60" : "text-white/55"
                    }`}
                  >
                    {n.note}
                  </div>
                  {n.current && (
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#0044FF]/8 text-[#0044FF] font-mono text-[10px] tracking-[0.18em] px-2 py-0.5 border border-[#0044FF]/15">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#0044FF]" />
                      YOU ARE HERE
                    </div>
                  )}
                </div>

                {i < NODES.length - 1 && (
                  <ArrowRight
                    className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 h-4 w-4 text-white/30"
                    strokeWidth={2}
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>

        <p className="mt-10 text-[13.5px] text-white/55 max-w-2xl">
          From the first sourcing request to delivered cargo — RFQ™ is the
          beginning of one continuous workflow. No tools to switch. No data to
          re-enter. No procurement gaps.
        </p>
      </div>
    </section>
  );
}
